import {
  AdminLibraryObject,
  CreateLibraryVersionRequest,
  LibraryDraftSpaceResponse,
  LibraryVersion,
  UploadLibraryResourceResponse,
} from "../../model/library";
import {
  protectedDelete,
  protectedGet,
  protectedPatch,
  protectedPost,
  protectedPut,
} from "../restUtil";
import ContentNavigator from "./ContentNavigator";
import _ from "underscore";
import { getAWSConfig } from "../config";
import AWS from "aws-sdk";
import S3 from "aws-sdk/clients/s3";

/*
    Implementation of the content navigator service. It utilizes version-based (or draft-based) content system,
    where all of the content modification managed by backend.
 */
export default class BackendContentNavigator implements ContentNavigator {
  bucket: string = "";
  s3Service: S3 | null = null;
  checkCredentialsPromise: Promise<object> | null = null;

  refreshToken(resolve: any, reject: any): void {
    getAWSConfig()
      .then((resp: any) => {
        AWS.config.credentials = new AWS.CognitoIdentityCredentials(
          {
            IdentityPoolId: resp.cognito.aws_cognito_identity_pool_id,
            IdentityId: resp.cognito.aws_cognito_identity_id,
            Logins: {
              "cognito-identity.amazonaws.com": resp.cognito.aws_cognito_token,
            },
          },
          {
            region: resp.region,
          }
        );
        AWS.config.update({
          region: resp.region,
        });
        this.bucket = resp.bucket;
        this.s3Service = new S3();
      })
      .then(resolve)
      .catch(reject);
  }

  checkCredentials(): Promise<object> {
    if (this.checkCredentialsPromise) {
      return this.checkCredentialsPromise;
    }

    this.checkCredentialsPromise = new Promise((resolve, reject) => {
      if (!AWS.config.credentials) {
        this.refreshToken(resolve, reject);
      } else {
        (AWS.config.credentials as AWS.CognitoIdentityCredentials).get(err => {
          if ((AWS!.config!.credentials! as AWS.CognitoIdentityCredentials).expireTime) {
            sessionStorage.setItem(
              "aws_token_expiration_time",
              (
                AWS!.config!.credentials! as AWS.CognitoIdentityCredentials
              ).expireTime.toISOString()
            );
          }
          if (err || (AWS.config.credentials as AWS.CognitoIdentityCredentials).expired) {
            this.refreshToken(resolve, reject);
          } else {
            resolve({});
          }
        });
      }
    });
    return this.checkCredentialsPromise;
  }

  getResourceURL(key: string, resource: string): Promise<string> {
    const objectKey = `${key}/assets/${resource}`;
    return this.checkCredentials().then(() =>
      this.s3Service!.getSignedUrlPromise("getObject", {
        Bucket: this.bucket,
        Key: objectKey,
      })
    );
  }

  getVersion(libraryVersionId: string): Promise<LibraryVersion> {
    const url = `/admin/library/version/${libraryVersionId}`;
    return new Promise((resolve, reject) => {
      protectedGet(url, undefined, resolve, reject);
    });
  }

  putContentFile(libraryVersionId: string, xml: string): Promise<void> {
    const url = `/admin/library/version/${libraryVersionId}/html`;
    return new Promise((resolve, reject) => {
      protectedPut(url, xml, resolve, reject, { contentType: "application/xml" });
    });
  }

  fetchEditorFile(libraryVersionId?: string, libraryObjectId?: string): Promise<any> {
    const url = libraryVersionId
      ? `/admin/library/version/${libraryVersionId}/editor`
      : `/admin/library/${libraryObjectId}/editor`;
    return new Promise((resolve, reject) =>
      protectedGet(url, undefined, resolve, reject)
    );
  }

  putEditorFile(libraryVersionId: string, editor: any): Promise<void> {
    const url = `/admin/library/version/${libraryVersionId}/editor`;
    return new Promise((resolve, reject) => {
      protectedPut(url, editor, resolve, reject);
    });
  }

  listObjects(
    libraryObjectId?: string | null,
    libraryVersionId?: string | null
  ): Promise<LibraryDraftSpaceResponse> {
    return new Promise<LibraryDraftSpaceResponse>((resolve, reject) => {
      const query = {
        parent_library_object_id: libraryObjectId,
        parent_library_version_id: libraryVersionId,
      };
      protectedGet(
        "/admin/library/versions",
        query,
        (response: any) => {
          resolve(response);
        },
        (err: any) => reject(err)
      );
    });
  }

  createVersion(
    libraryObjectId?: string,
    update?: Partial<CreateLibraryVersionRequest>
  ): Promise<LibraryVersion> {
    return new Promise((resolve, reject) => {
      const url = "/admin/library/versions";
      const body: Partial<LibraryVersion> & { original_id?: string } = {};
      if (libraryObjectId) {
        body.original_id = libraryObjectId;
      }
      if (!_.isEmpty(update)) {
        Object.assign(body, update);
      }
      protectedPost(url, body, resolve, reject);
    });
  }

  deleteVersion(libraryVersionId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = `/admin/library/version/${libraryVersionId}`;
      protectedDelete(url, undefined, resolve, reject);
    });
  }

  editVersion(
    libraryVersionId: string,
    update: Partial<LibraryVersion>
  ): Promise<LibraryVersion> {
    return new Promise((resolve, reject) => {
      const url = `/admin/library/version/${libraryVersionId}`;
      protectedPatch(url, update, resolve, reject);
    });
  }

  getUploadResourceUrl(
    libraryVersionId: string,
    filesMeta: { name: string; type: string }[]
  ): Promise<UploadLibraryResourceResponse> {
    return new Promise((resolve, reject) => {
      const url = `/admin/library/version/${libraryVersionId}/content/resources`;
      const payload = {
        resources: filesMeta.map(item => {
          return { name: item.name, content_type: item.type };
        }),
      };
      protectedPost(url, payload, resolve, reject);
    });
  }

  uploadResource(uploadUrl: string, file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = true;
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.onreadystatechange = function () {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject("Error uploading file.");
          }
        }
      };
      xhr.send(file);
    });
  }

  publishVersion(libraryVersionId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = `/admin/library/version/${libraryVersionId}/publish`;
      protectedPost(url, undefined, resolve, reject);
    });
  }

  uploadPreview(
    libraryVersionId: string,
    type: "regular" | "vertical" | "horizontal" | "icon",
    preview: File
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = `/admin/library/version/${libraryVersionId}/preview/${type}`;
      protectedPost(url, preview, resolve, reject, { contentType: preview.type });
    });
  }

  getObject(libraryObjectId: string): Promise<AdminLibraryObject> {
    return new Promise((resolve, reject) => {
      const url = `/library/${libraryObjectId}`;
      protectedGet(url, undefined, resolve, reject);
    });
  }

  deleteLibraryObject(libraryObjectId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = `/admin/library/${libraryObjectId}`;
      protectedDelete(url, undefined, resolve, reject);
    });
  }
}
